import { readEditorProbe } from './editorProbe';

// ── La capture de l'ecran, pour le support (P03) ──
// 1. La capture du navigateur : il demande l'onglet a partager, une image est tiree de la video, puis le partage
//    s'arrete aussitot.
// 2. Le secours, sans getDisplayMedia : la page reconstruite en image (modern-screenshot), la vue 3D rendue a part
//    et collee a sa place.
// Un refus de l'utilisateur n'est pas une erreur : 'cancelled'.

export type CaptureResult = { blob: Blob; fallback: boolean } | 'cancelled';

const MAX_WIDTH = 1920;
const QUALITY = 0.85;

/** Reduit a 1 920 px de large au plus, en WebP (PNG si le navigateur ne sait pas l'ecrire) */
export async function toWebp(source: Blob | HTMLCanvasElement, maxWidth = MAX_WIDTH): Promise<Blob> {
  const image = source instanceof Blob ? await createImageBitmap(source) : source;
  const scale = Math.min(1, maxWidth / image.width);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(image.width * scale);
  canvas.height = Math.round(image.height * scale);
  canvas.getContext('2d')!.drawImage(image, 0, 0, canvas.width, canvas.height);
  if ('close' in image) image.close();
  return new Promise((resolve, reject) =>
    canvas.toBlob(b => (b ? resolve(b) : reject(new Error('Image illisible'))), 'image/webp', QUALITY));
}

/** Un refus aussi rapide n'a pas laisse le temps de choisir : la capture est bloquee (politique, permission du systeme) */
const BLOCKED_WITHIN_MS = 300;

class CaptureCancelled extends Error {}

/** Une image de l'onglet partage, puis toutes les pistes arretees */
async function fromDisplayMedia(): Promise<Blob> {
  const asked = performance.now();
  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia({
      video: { displaySurface: 'browser' },
      audio: false,
      // Options de Chrome : proposer cet onglet en premier
      preferCurrentTab: true,
      selfBrowserSurface: 'include',
    } as DisplayMediaStreamOptions);
  } catch (err) {
    const refused = err instanceof DOMException && (err.name === 'NotAllowedError' || err.name === 'AbortError');
    if (refused && performance.now() - asked > BLOCKED_WITHIN_MS) throw new CaptureCancelled();
    throw err;
  }
  try {
    const video = document.createElement('video');
    video.muted = true;
    video.srcObject = stream;
    await video.play();
    // Une vraie image, pas la premiere trame vide
    await new Promise<void>(resolve => {
      if ('requestVideoFrameCallback' in video) video.requestVideoFrameCallback(() => resolve());
      else setTimeout(resolve, 150);
    });
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')!.drawImage(video, 0, 0);
    video.srcObject = null;
    return toWebp(canvas);
  } finally {
    stream.getTracks().forEach(t => t.stop());
  }
}

const loadImage = (src: string) => new Promise<HTMLImageElement>((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error('Image illisible'));
  img.src = src;
});

/** Le secours : la page en image, la vue 3D collee par-dessus son canvas */
async function fromPage(): Promise<Blob> {
  const { domToCanvas } = await import('modern-screenshot');
  const width = window.innerWidth;
  const height = window.innerHeight;
  const view3d = readEditorProbe()?.renderToDataUrl?.();
  const canvas = await domToCanvas(document.body, {
    width, height, scale: 1,
    // Le support ne figure pas sur l'image
    filter: node => !(node instanceof HTMLElement && node.dataset.support),
  });
  if (view3d) {
    const img = await loadImage(view3d.dataUrl);
    // La page est prise depuis son haut : le rectangle de la vue 3D, lu dans la fenetre, suit le defilement
    const { left, top, width: w, height: h } = view3d.rect;
    canvas.getContext('2d')!.drawImage(img, left + window.scrollX, top + window.scrollY, w, h);
  }
  return toWebp(canvas);
}

export const canCaptureTab = () => typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia;

export async function captureTab(): Promise<CaptureResult> {
  if (canCaptureTab()) {
    try {
      return { blob: await fromDisplayMedia(), fallback: false };
    } catch (err) {
      if (err instanceof CaptureCancelled) return 'cancelled';
      // Capture impossible pour une autre raison (bloquee, politique du navigateur…) : le secours
    }
  }
  return { blob: await fromPage(), fallback: true };
}
